import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import { ManageLeadColumnsDialogProps } from './types';
import { MANAGE_COLUMNS_DIALOG_MAX_WIDTH, getManageColumnsDialogPaperProps } from '../../../utils/leads/leadsUtils';
import { useManageLeadColumns } from './useManageLeadColumns';
import { ReorderColumnsTab } from './ReorderColumnsTab';
import { CreateColumnTab } from './CreateColumnTab';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { EditChoiceColumnDialog } from './EditChoiceColumnDialog';

export const ManageLeadColumnsDialog: React.FC<ManageLeadColumnsDialogProps> = (props) => {
  const { open, onClose, customColumns } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    activeTab,
    setActiveTab,
    columnsState,
    isUserModified,
    highlightedKey,
    newColName,
    setNewColName,
    newColType,
    optionsList,
    currentOptionInput,
    setCurrentOptionInput,
    nameError,
    setNameError,
    optionsError,
    setOptionsError,
    createOptionErrors,
    handleCreateOptionRename,
    editingKey,
    setEditingKey,
    editingName,
    setEditingName,
    editingColumnType,
    renameError,
    setRenameError,
    editingOptions,
    initialEditingOptions,
    initialEditingOptionsCount,
    editingOptionInput,
    setEditingOptionInput,
    editingAddError,
    setEditingAddError,
    editingOptionErrors,
    isChoiceDialogOpen,
    handleCloseChoiceDialog,
    isChoiceModified,
    hasOptionRenames,
    deletingCol,
    setDeletingCol,
    sensors,
    handleDragEnd,
    handleToggleVisibility,
    handleSaveConfig,
    handleAddOption,
    handleRemoveOption,
    handleAddEditingOption,
    handleEditingOptionRename,
    handleRemoveEditingOption,
    handleTypeChange,
    handleCreateColumn,
    handleStartRename,
    handleSaveRename,
    handleInitiateDeactivate,
    handleConfirmDeactivate,
  } = useManageLeadColumns(props);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={MANAGE_COLUMNS_DIALOG_MAX_WIDTH}
      fullWidth
      fullScreen={isMobile}
      PaperProps={getManageColumnsDialogPaperProps(isMobile)}
    >
      <DialogTitle sx={{ flexShrink: 0, pt: 2.5, px: 3, pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: '-0.025em', color: 'text.primary' }}>
            Manage Columns
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setActiveTab(value);
            }
          }}
          fullWidth
          sx={{
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
            p: 0.5,
            borderRadius: '10px',
            border: 'none',
            mb: 1,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              '&:not(:first-of-type)': { borderRadius: '8px' },
              '&:first-of-type': { borderRadius: '8px' },
            },
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'background.paper' : '#fff'),
                color: 'primary.main',
                fontWeight: 600,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                '&:hover': {
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'background.paper' : '#fff'),
                  color: 'primary.main',
                },
              },
              '&:hover': {
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
              },
            },
          }}
        >
          <ToggleButton value={1} sx={{ textTransform: 'none', py: 0.75, fontSize: '0.875rem' }}>
            Create Column
          </ToggleButton>
          <ToggleButton value={0} sx={{ textTransform: 'none', py: 0.75, fontSize: '0.875rem' }}>
            Edit Column
          </ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        {activeTab === 0 && (
          <ReorderColumnsTab
            sensors={sensors}
            columnsState={columnsState}
            customColumns={customColumns}
            handleDragEnd={handleDragEnd}
            handleToggleVisibility={handleToggleVisibility}
            handleStartRename={handleStartRename}
            handleInitiateDeactivate={handleInitiateDeactivate}
            editingKey={editingKey}
            editingName={editingName}
            renameError={renameError}
            setEditingName={setEditingName}
            setRenameError={setRenameError}
            handleSaveRename={handleSaveRename}
            setEditingKey={setEditingKey}
            highlightedKey={highlightedKey}
            isChoiceDialogOpen={isChoiceDialogOpen}
          />
        )}

        {activeTab === 1 && (
          <CreateColumnTab
            handleCreateColumn={handleCreateColumn}
            newColName={newColName}
            setNewColName={setNewColName}
            nameError={nameError}
            setNameError={setNameError}
            newColType={newColType}
            handleTypeChange={handleTypeChange}
            optionsError={optionsError}
            setOptionsError={setOptionsError}
            currentOptionInput={currentOptionInput}
            setCurrentOptionInput={setCurrentOptionInput}
            handleAddOption={handleAddOption}
            optionsList={optionsList}
            handleRemoveOption={handleRemoveOption}
            handleOptionRename={handleCreateOptionRename}
            createOptionErrors={createOptionErrors}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Cancel</Button>
        {activeTab === 0 ? (
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            startIcon={<SaveIcon />}
            disabled={!isUserModified}
          >
            Save
          </Button>
        ) : (
          <Button
            type="submit"
            form="create-custom-column-form"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        )}
      </DialogActions>

      <DeleteConfirmDialog
        deletingCol={deletingCol}
        onClose={() => setDeletingCol(null)}
        onConfirm={handleConfirmDeactivate}
      />

      <EditChoiceColumnDialog
        open={isChoiceDialogOpen}
        onClose={handleCloseChoiceDialog}
        columnType={editingColumnType}
        editingName={editingName}
        onEditingNameChange={(val) => {
          setEditingName(val);
          if (renameError) setRenameError(null);
        }}
        renameError={renameError}
        options={editingOptions}
        initialOptions={initialEditingOptions}
        initialCount={initialEditingOptionsCount}
        addInput={editingOptionInput}
        onAddInputChange={setEditingOptionInput}
        onAddOption={handleAddEditingOption}
        addError={editingAddError}
        onClearAddError={() => setEditingAddError(null)}
        onOptionRename={handleEditingOptionRename}
        onRemoveOption={handleRemoveEditingOption}
        optionErrors={editingOptionErrors}
        onSave={handleSaveRename}
        isModified={isChoiceModified}
        hasOptionRenames={hasOptionRenames}
      />
    </Dialog>
  );
};
